import React from 'react';
import bizCalc1 from '../../assets/home/ecosystem/biz-calc1.png';
import bizCalc2 from '../../assets/home/ecosystem/biz-calc2.png';
import challenge1 from '../../assets/home/ecosystem/challenge-1.png';
import challenge2 from '../../assets/home/ecosystem/challenge-2.png';
import climateEquity from '../../assets/home/ecosystem/climate-equity.png';
import davisScenarios from '../../assets/home/ecosystem/davis-scenarios.png';
import householdCalc1 from '../../assets/home/ecosystem/household-calc1.png';
import householdCalc2 from '../../assets/home/ecosystem/household-calc2.png';
import customizeVmtData from '../../assets/home/featured-tools/customize-vmt-data.png';
import advancedAnalysis from '../../assets/home/featured-tools/advanced-analysis.png';
import davisPolicy from '../../assets/home/featured-tools/davis-policy.png';
import elecData from '../../assets/home/featured-tools/elec-data.png';
import electricityDownload from '../../assets/home/featured-tools/electricity-download.png';
import electricityPng from '../../assets/home/featured-tools/electricity-png.png';
import energyScenarios from '../../assets/home/featured-tools/energy-scenarios.png';
import relatedPolicies from '../../assets/home/featured-tools/related-policies.png';
import scenarios from '../../assets/home/featured-tools/scenarios.png';
import sfData from '../../assets/home/featured-tools/sf-data.png';
import sfEnvironment from '../../assets/home/featured-tools/sf-environment.png';
import vmtModifiedChart from '../../assets/home/featured-tools/vmt-modified-chart.png';
import keyMetrics from '../../assets/home/featured-tools/key-metrics.png';
import adminSettings from '../../assets/home/featured-tools/admin-settings.png';
import cbei1 from '../../assets/home/featured-tools/cbei1.png';
import cbei2 from '../../assets/home/featured-tools/cbei2.png';
import { HomeSectionShell } from './HomeSectionShell';
import {
  FEATURED_MEMBER_TOOLS,
  FEATURED_TOOL_MEMBERSHIP_LABELS,
  FEATURED_TOOLS_SECTION,
} from './homePageConstants';

type ToolScreenshot = {
  primary: string;
  hover?: string;
  primaryAlt: string;
  hoverAlt?: string;
};

const TOOL_SCREENSHOTS: Record<string, ToolScreenshot> = {
  'data-download': {
    primary: electricityDownload,
    hover: elecData,
    primaryAlt: 'Download Data modal with electricity datasets selected',
    hoverAlt: 'Electricity consumption data table export preview',
  },
  'custom-portal': {
    primary: sfData,
    hover: sfEnvironment,
    primaryAlt: 'Custom portal data and indicators for a member jurisdiction',
    hoverAlt: 'Custom portal environment and climate content section',
  },
  'advanced-analysis': {
    primary: advancedAnalysis,
    hover: electricityPng,
    primaryAlt: 'Statewide analysis and downloadable GHG inventory reports',
    hoverAlt: 'Electricity consumption chart export preview',
  },
  'local-government-workspace': {
    primary: vmtModifiedChart,
    hover: customizeVmtData,
    primaryAlt: 'VMT chart updated after customizing values in the data table',
    hoverAlt: 'Customizable VMT data table for local GHG alignment',
  },
  'consumption-based-ghg-inventory': {
    primary: cbei1,
    hover: cbei2,
    primaryAlt: 'Consumption-based GHG inventory overview',
    hoverAlt: 'Tract-level consumption-based emissions detail view',
  },
  'advanced-local-government-admin': {
    primary: keyMetrics,
    hover: adminSettings,
    primaryAlt: 'Key metrics dashboard for local government admin',
    hoverAlt: 'Admin settings for local government workspace',
  },
  'policy-explorer': {
    primary: davisPolicy,
    hover: relatedPolicies,
    primaryAlt: 'Policy Explorer showing adopted policies for a jurisdiction',
    hoverAlt: 'Related policies panel with peer community comparisons',
  },
  'scenario-planning': {
    primary: scenarios,
    hover: energyScenarios,
    primaryAlt: 'Scenario comparison view for future emissions pathways',
    hoverAlt: 'Energy scenario planning chart with policy impacts',
  },
  climateplans: {
    primary: davisScenarios,
    hover: climateEquity,
    primaryAlt: 'ClimatePlans scenario planning view for Davis',
    hoverAlt: 'ClimatePlans climate equity indicators view',
  },
  calculator: {
    primary: householdCalc1,
    hover: householdCalc2,
    primaryAlt: 'CoolClimate Household Calculator overview',
    hoverAlt: 'CoolClimate Household Calculator results view',
  },
  business: {
    primary: bizCalc1,
    hover: bizCalc2,
    primaryAlt: 'CoolClimate Calculator for Businesses overview',
    hoverAlt: 'CoolClimate Calculator for Businesses results view',
  },
  challenge: {
    primary: challenge1,
    hover: challenge2,
    primaryAlt: 'Cool Campus Challenge overview',
    hoverAlt: 'Cool Campus Challenge leaderboard and engagement view',
  },
};

export const FeaturedToolsSection: React.FC = () => (
  <HomeSectionShell
    id="featured-tools"
    title={FEATURED_TOOLS_SECTION.title}
    subtitle={FEATURED_TOOLS_SECTION.subtitle}
  >
    <div className="home-card-grid home-card-grid--three home-feature-grid">
      {FEATURED_MEMBER_TOOLS.map((tool) => {
        const screenshots = TOOL_SCREENSHOTS[tool.id];
        const hasHoverSwap = Boolean(screenshots?.hover);
        const membershipLevel = 'membershipLevel' in tool ? tool.membershipLevel : undefined;

        return (
          <article key={tool.id} className="home-feature-card">
            <div
              className={`home-feature-card__screenshot${
                screenshots ? ' home-feature-card__screenshot--image' : ''
              }${hasHoverSwap ? ' home-feature-card__screenshot--swap' : ''}`}
            >
              {screenshots ? (
                <>
                  <img
                    src={screenshots.primary}
                    alt={screenshots.primaryAlt}
                    className="home-feature-card__screenshot-img home-feature-card__screenshot-img--primary"
                    loading="lazy"
                    decoding="async"
                  />
                  {screenshots.hover ? (
                    <img
                      src={screenshots.hover}
                      alt={screenshots.hoverAlt || screenshots.primaryAlt}
                      className="home-feature-card__screenshot-img home-feature-card__screenshot-img--hover"
                      loading="lazy"
                      decoding="async"
                    />
                  ) : null}
                  {hasHoverSwap ? (
                    <span className="home-feature-card__screenshot-hint">Hover to preview</span>
                  ) : null}
                </>
              ) : (
                <span className="home-feature-card__screenshot-label">Screenshot</span>
              )}
            </div>
            {membershipLevel ? (
              <span
                className={`home-feature-card__badge home-feature-card__badge--${membershipLevel}`}
              >
                {FEATURED_TOOL_MEMBERSHIP_LABELS[membershipLevel]}
              </span>
            ) : null}
            {'membersOnly' in tool && tool.membersOnly ? (
              <span className="home-feature-card__badge home-feature-card__badge--members">
                Members
              </span>
            ) : null}
            <h3 className="home-feature-card__title">{tool.title}</h3>
            {Array.isArray(tool.description) ? (
              <ul className="home-feature-card__desc home-feature-card__desc-list">
                {tool.description.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            ) : (
              <p className="home-feature-card__desc">{tool.description}</p>
            )}
          </article>
        );
      })}
    </div>
  </HomeSectionShell>
);
